import { useEffect, useRef } from "react";
import { loadGoogleMaps } from "../lib/maps";
import { useIsMobile } from "../lib/useIsMobile";

// Inline map for a single technician's optimized round trip.
// Draws the HOME marker (Start/Home), each stop as a numbered marker in
// optimized visit order, and a Polyline connecting home -> stops -> home.
// Mounts lazily (only rendered when the tech's "Show map" is toggled) so we
// never have several Google maps spun up at once.
//
// Props:
//   home    = { lat, lng }          tech's home (start + end)
//   stops   = [{ lat, lng, label }] ordered stops (label = client name)
export function RouteMap({ home, stops }) {
  const isMobile = useIsMobile();
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  // Re-run ONLY when the actual coordinates change — not on every parent
  // re-render (e.g. completing a stop), which would re-fit bounds and flicker.
  const sig = JSON.stringify([home && [home.lat, home.lng], (stops || []).map((s) => [s.lat, s.lng])]);

  useEffect(() => {
    if (!mapRef.current || !home) return;

    let cancelled = false;
    loadGoogleMaps().then(() => {
      if (cancelled || !mapRef.current) return;

      if (!mapObj.current) {
        mapObj.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: Number(home.lat), lng: Number(home.lng) },
          zoom: 11,
          mapTypeId: "roadmap",
          styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
          disableDefaultUI: false,
          streetViewControl: false,
          mapTypeControl: false,
          // On a phone the page itself scrolls, so Google's default ("cooperative") turns a
          // one-finger drag into a page scroll + a "use two fingers" nag. This inline map is
          // opened deliberately via "Show map", so let one finger pan it. Desktop keeps the
          // default ("auto" = cooperative-free mouse handling, unchanged).
          gestureHandling: isMobile ? "greedy" : "auto",
        });
      } else {
        mapObj.current.setOptions({ gestureHandling: isMobile ? "greedy" : "auto" });
      }

      // Clear any prior overlays.
      if (mapObj.current._overlays) mapObj.current._overlays.forEach((o) => o.setMap(null));
      mapObj.current._overlays = [];

      const bounds = new window.google.maps.LatLngBounds();
      const homePos = { lat: Number(home.lat), lng: Number(home.lng) };

      // Polyline path: home -> ordered stops -> home (round trip).
      const path = [homePos];
      stops.forEach((s) => path.push({ lat: Number(s.lat), lng: Number(s.lng) }));
      path.push(homePos);

      const line = new window.google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: "#0071E3",
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map: mapObj.current,
      });
      mapObj.current._overlays.push(line);
      path.forEach((p) => bounds.extend(p));

      // HOME marker — distinct house pin labeled Start/Home.
      const homeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
        <circle cx="15" cy="15" r="13" fill="#1A8A52" stroke="white" stroke-width="2"/>
        <path d="M15 7l7 6h-2v8h-4v-5h-2v5H8v-8H6z" fill="white"/>
      </svg>`;
      const homeMarker = new window.google.maps.Marker({
        position: homePos,
        map: mapObj.current,
        title: "Start / Home",
        zIndex: 1000,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(homeSvg)}`,
          scaledSize: new window.google.maps.Size(30, 30),
          anchor: new window.google.maps.Point(15, 15),
        },
      });
      const homeIw = new window.google.maps.InfoWindow({
        content: `<div style="font-size:12px;padding:2px"><strong>Start / Home</strong></div>`,
      });
      homeMarker.addListener("click", () => homeIw.open(mapObj.current, homeMarker));
      mapObj.current._overlays.push(homeMarker);

      // Numbered stop markers in optimized order.
      stops.forEach((s, i) => {
        const pos = { lat: Number(s.lat), lng: Number(s.lng) };
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26">
          <circle cx="13" cy="13" r="12" fill="#0071E3" stroke="white" stroke-width="2"/>
          <text x="13" y="17" text-anchor="middle" font-size="12" font-weight="700" fill="white" font-family="Arial">${i + 1}</text>
        </svg>`;
        const marker = new window.google.maps.Marker({
          position: pos,
          map: mapObj.current,
          title: `${i + 1}. ${s.label || ""}`,
          zIndex: 10,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new window.google.maps.Size(26, 26),
            anchor: new window.google.maps.Point(13, 13),
          },
        });
        const iw = new window.google.maps.InfoWindow({
          content: `<div style="font-size:12px;padding:2px"><strong>${i + 1}. ${s.label || "Stop"}</strong></div>`,
        });
        marker.addListener("click", () => iw.open(mapObj.current, marker));
        bounds.extend(pos);
        mapObj.current._overlays.push(marker);
      });

      if (!bounds.isEmpty()) mapObj.current.fitBounds(bounds, 48);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  return (
    <div
      ref={mapRef}
      style={{ width: "100%", height: 320, borderRadius: "var(--r)", background: "#e8eaed", marginTop: 12 }}
    />
  );
}
